// src/features/auth/VerifyResetOTP.jsx
//
// Step 2 of the OTP-gated password reset flow.
// Accepts the 6-digit code → POST /auth/verify-reset-otp
// On success: stores reset_token in navigate state → /reset-password
//
// UX features:
//   • Auto-focuses first input; auto-advances on each digit
//   • 30-second resend countdown backed by server cooldown
//   • Attempt counter surfaced to the user
//   • Expired / locked OTP triggers a clear CTA to go back and re-request

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate }           from 'react-router-dom';
import { ArrowLeft, RefreshCw, ShieldCheck }        from 'lucide-react';
import { motion, AnimatePresence }                  from 'framer-motion';
import { Button }  from '@/shared/components/ui/button';
import { authAPI } from '@/api/auth.api';

// ─── Constants ────────────────────────────────────────────────────────────────

const OTP_LENGTH        = 6;
const RESEND_COOLDOWN_S = 30;

// ─── Digit input sub-component ────────────────────────────────────────────────

function OTPInput({ digits, onChange, disabled }) {
  const refs = useRef([]);

  const handleKey = (e, idx) => {
    if (e.key === 'Backspace') {
      if (digits[idx]) {
        onChange(idx, '');
      } else if (idx > 0) {
        refs.current[idx - 1]?.focus();
        onChange(idx - 1, '');
      }
      return;
    }
    if (e.key === 'ArrowLeft' && idx > 0)            { refs.current[idx - 1]?.focus(); return; }
    if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) { refs.current[idx + 1]?.focus(); return; }
  };

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, '').slice(-1);
    onChange(idx, val);
    if (val && idx < OTP_LENGTH - 1) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    pasted.split('').forEach((ch, i) => onChange(i, ch));
    const nextEmpty = Math.min(pasted.length, OTP_LENGTH - 1);
    refs.current[nextEmpty]?.focus();
  };

  return (
    <div className="flex gap-3 justify-center" onPaste={handlePaste}>
      {Array.from({ length: OTP_LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i]}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKey(e, i)}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          className={[
            'w-12 h-14 text-center text-xl font-bold rounded-lg border-2 outline-none',
            'transition-colors focus:border-antique-gold focus:ring-2 focus:ring-antique-gold/20',
            digits[i] ? 'border-antique-gold bg-antique-gold/5' : 'border-gray-200 bg-white',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
          ].join(' ')}
        />
      ))}
    </div>
  );
}

// ─── Resend countdown ─────────────────────────────────────────────────────────

function useResendTimer(initialSeconds = RESEND_COOLDOWN_S) {
  const [seconds, setSeconds] = useState(initialSeconds);

  useEffect(() => {
    if (seconds <= 0) return;
    const id = setInterval(() => setSeconds((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [seconds]);

  const reset = useCallback((s = RESEND_COOLDOWN_S) => setSeconds(s), []);

  return { seconds, canResend: seconds <= 0, reset };
}

// ─── Views ────────────────────────────────────────────────────────────────────

const VIEW = {
  VERIFY:   'verify',
  LOCKED:   'locked',    // max attempts hit
  EXPIRED:  'expired',   // OTP expired
  SUCCESS:  'success',   // not shown long — navigates away
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function VerifyResetOTP() {
  const location = useLocation();
  const navigate  = useNavigate();

  // Email passed via navigate state from ForgotPassword
  const email = location.state?.email || '';

  const [view,         setView]         = useState(VIEW.VERIFY);
  const [digits,       setDigits]       = useState(Array(OTP_LENGTH).fill(''));
  const [error,        setError]        = useState('');
  const [isVerifying,  setIsVerifying]  = useState(false);
  const [isResending,  setIsResending]  = useState(false);
  const { seconds, canResend, reset: resetTimer } = useResendTimer(RESEND_COOLDOWN_S);

  // If landed here without an email, bounce back
  useEffect(() => {
    if (!email) navigate('/forgot-password', { replace: true });
  }, [email, navigate]);

  const otp = digits.join('');
  const otpComplete = otp.length === OTP_LENGTH;

  // ── Digit update ─────────────────────────────────────────────────────────

  const handleDigitChange = (idx, val) => {
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
    if (error) setError('');
  };

  // ── Verify ───────────────────────────────────────────────────────────────

  const handleVerify = async () => {
    if (!otpComplete) { setError('Please enter all 6 digits.'); return; }
    setError('');
    setIsVerifying(true);

    try {
      const { reset_token } = await authAPI.verifyResetOTP({ email, otp });
      // Navigate to Step 3, passing the reset_token via state (never in URL)
      navigate('/reset-password', {
        state: { email, reset_token },
        replace: true,
      });
    } catch (err) {
      const status  = err?.response?.status;
      const message = err?.response?.data?.detail || '';

      if (status === 429) {
        if (message.toLowerCase().includes('locked') || message.toLowerCase().includes('attempt')) {
          setView(VIEW.LOCKED);
        } else {
          setError('Too many requests. Please wait and try again.');
        }
      } else if (status === 401) {
        if (message.toLowerCase().includes('expired')) {
          setView(VIEW.EXPIRED);
        } else if (message.toLowerCase().includes('locked') || message.toLowerCase().includes('too many')) {
          setView(VIEW.LOCKED);
        } else {
          setError(message || 'Incorrect code. Please try again.');
          // Clear digits for retry
          setDigits(Array(OTP_LENGTH).fill(''));
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // ── Resend ───────────────────────────────────────────────────────────────

  const handleResend = async () => {
    if (!canResend || isResending) return;
    setIsResending(true);
    setError('');
    setDigits(Array(OTP_LENGTH).fill(''));

    try {
      const data = await authAPI.resendResetOTP(email);
      // Server may return a non-zero cooldown_remaining if it adjusted the timer
      const serverCooldown = data?.cooldown_remaining || RESEND_COOLDOWN_S;
      resetTimer(serverCooldown);
      setView(VIEW.VERIFY);
    } catch (err) {
      const status  = err?.response?.status;
      const message = err?.response?.data?.detail || '';
      if (status === 429) {
        // Server told us how long to wait
        const match = message.match(/(\d+)\s*second/i);
        if (match) resetTimer(parseInt(match[1], 10));
        else        resetTimer(RESEND_COOLDOWN_S);
        setError('Please wait before requesting another code.');
      } else {
        setError('Could not resend the code. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '*'.repeat(b.length) + c)
    : '';

  return (
    <div className="min-h-screen flex">

      {/* Left panel */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1602173574767-37ac01994b2a?w=1200&q=80"
          alt="Gawher Ali Jewellery"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 flex items-center justify-center text-white text-center p-8">
          <div>
            <h2 className="font-playfair text-4xl mb-4">Gawher Ali Jewellery</h2>
            <p className="text-lg opacity-90">Handcrafted jewellery — premium quality at honest prices</p>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <AnimatePresence mode="wait">

          {/* ── VERIFY view ──────────────────────────────────────────────── */}
          {view === VIEW.VERIFY && (
            <motion.div
              key="verify"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="w-full max-w-md"
            >
              <Link
                to="/forgot-password"
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-antique-gold mb-8 transition-colors"
              >
                <ArrowLeft size={15} />
                Change email
              </Link>

              <div className="mb-8">
                <div className="w-12 h-12 rounded-full bg-antique-gold/10 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-antique-gold" />
                </div>
                <h1 className="font-playfair text-3xl mb-2">Enter your reset code</h1>
                <p className="text-gray-500 text-sm leading-relaxed">
                  We sent a 6-digit code to{' '}
                  <span className="font-medium text-gray-800">{maskedEmail}</span>.
                  {' '}It expires in 5 minutes.
                </p>
              </div>

              {/* OTP digit inputs */}
              <div className="mb-6">
                <OTPInput
                  digits={digits}
                  onChange={handleDigitChange}
                  disabled={isVerifying}
                />
              </div>

              {/* Error */}
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 text-center text-sm text-red-600"
                >
                  {error}
                </motion.p>
              )}

              {/* Verify button */}
              <Button
                onClick={handleVerify}
                disabled={!otpComplete || isVerifying}
                className="w-full btn-gold mb-5"
              >
                {isVerifying ? (
                  <span className="animate-pulse">Verifying…</span>
                ) : (
                  'Verify Code'
                )}
              </Button>

              {/* Resend */}
              <div className="text-center">
                {canResend ? (
                  <button
                    onClick={handleResend}
                    disabled={isResending}
                    className="inline-flex items-center gap-1.5 text-sm text-antique-gold hover:underline disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={isResending ? 'animate-spin' : ''} />
                    {isResending ? 'Resending…' : 'Resend code'}
                  </button>
                ) : (
                  <p className="text-sm text-gray-400">
                    Resend code in{' '}
                    <span className="font-medium text-gray-600 tabular-nums">
                      {String(Math.floor(seconds / 60)).padStart(2, '0')}:
                      {String(seconds % 60).padStart(2, '0')}
                    </span>
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* ── LOCKED view (max attempts) ───────────────────────────────── */}
          {view === VIEW.LOCKED && (
            <motion.div
              key="locked"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md text-center"
            >
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🔒</span>
              </div>
              <h1 className="font-playfair text-2xl mb-3">Too many attempts</h1>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                This reset code has been locked after too many incorrect attempts.
                Please request a new code to try again.
              </p>
              <Link to="/forgot-password">
                <Button className="w-full btn-gold">Request a new code</Button>
              </Link>
            </motion.div>
          )}

          {/* ── EXPIRED view ─────────────────────────────────────────────── */}
          {view === VIEW.EXPIRED && (
            <motion.div
              key="expired"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md text-center"
            >
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">⏱</span>
              </div>
              <h1 className="font-playfair text-2xl mb-3">Code expired</h1>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                Your reset code has expired (codes are valid for 5 minutes).
                Please request a fresh code.
              </p>
              <Link to="/forgot-password">
                <Button className="w-full btn-gold">Request a new code</Button>
              </Link>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}